import jsPDF from 'jspdf';
import { LabelTemplates } from './LabelTemplates';

/**
 * Generates a PDF with addresses formatted for Avery labels
 * Uses configuration from LabelTemplates.js
 * 
 * @param {Array} addresses - Array of address objects
 * @param {Object} options - Configuration options
 * @param {string} options.templateId - ID of the template to use (default: '18294')
 * @param {boolean} options.debugMode - If true, draws label boundaries
 * @param {number} options.horizontalOffset - Horizontal offset adjustment in mm (default: 0)
 * @param {number} options.verticalOffset - Vertical offset adjustment in mm (default: 0)
 * @param {number} options.fontSize - Font size for address text (default: 8)
 * @param {boolean} options.useCustomFont - Use 'Mountains of Christmas' font
 * @param {boolean} options.returnBlob - If true, returns blob URL instead of saving
 */
export const generateAvery5160PDF = async (addresses, options = {}) => {
  const {
    templateId = '18294',
    debugMode = false,
    horizontalOffset = 0,
    verticalOffset = 0,
    fontSize = 11, // Default increased for Mountains of Christmas (it's small)
    includeReturnLabels = false,
    useCustomFont = true,
    returnBlob = false
  } = options;

  const template = LabelTemplates[templateId] || LabelTemplates['18294'];
  const { page, layout, dimensions } = template;

  // Prepare Address List
  let addressesToPrint = [...addresses];

  if (includeReturnLabels) {
    const returnAddress = {
      name: "Dymock Family",
      address: "914 Blackstone Drive",
      address2: "", // Ensure consistent object structure
      city: "Allen",
      state: "TX",
      zip: "75002",
      isLittle: false // Avoid auto-scaling confusion
    };
    
    // Add a return label for EACH source address
    // We append them to the end, or interleave? User said "print the same number... ensures you are printing the same number".
    // Usually batch printing return labels at the end is better for efficiency, but interleaving might be what they mean by "toggle".
    // Re-reading plan: "Return labels will be appended **after** all destination labels".
    const returnLabels = addresses.map(() => ({ ...returnAddress }));
    addressesToPrint = [...addresses, ...returnLabels];
  }

  // Create PDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Load Custom Font
  if (useCustomFont) {
    try {
      // Fetch the font buffer
      // Mountains of Christmas Regular from GitHub Raw
      const response = await fetch('https://raw.githubusercontent.com/google/fonts/main/apache/mountainsofchristmas/MountainsofChristmas-Regular.ttf');
      if (!response.ok) throw new Error('Font fetch failed');

      const blob = await response.blob();
      const reader = new FileReader();
      
      await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve();
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      const base64Font = reader.result.split(',')[1];
      doc.addFileToVFS('MountainsOfChristmas-Regular.ttf', base64Font);
      doc.addFont('MountainsOfChristmas-Regular.ttf', 'MountainsOfChristmas', 'normal');
      doc.setFont('MountainsOfChristmas');
    } catch (error) {
      doc.setFont("helvetica");
    }
  } else {
    doc.setFont("helvetica");
  }

  const labelsPerPage = layout.cols * layout.rows;

  addressesToPrint.forEach((address, index) => {
    const pageIndex = Math.floor(index / labelsPerPage);
    const posOnPage = index % labelsPerPage;
    const col = posOnPage % layout.cols;
    const row = Math.floor(posOnPage / layout.cols);

    // Add new page if needed
    if (pageIndex > 0 && posOnPage === 0) {
      doc.addPage();
    }

    // Calculate absolute position based on template specs
    const x = dimensions.marginLeft + (col * (dimensions.labelWidth + dimensions.horizontalGap)) + horizontalOffset;
    const y = dimensions.marginTop + (row * (dimensions.labelHeight + dimensions.verticalGap)) + verticalOffset;

    // Debug mode
    if (debugMode) {
      drawDebugGuides(doc, x, y, dimensions, index, template);
      if (posOnPage === 0) drawPageGrid(doc, page);
    }

    // Render Address Text
    renderAddress(doc, address, x, y, dimensions, fontSize);
  });

  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `avery_${templateId}_labels_${timestamp}.pdf`;

  if (!returnBlob) {
    doc.save(filename);
  }

  return {
    success: true,
    filename,
    labelCount: addressesToPrint.length,
    pageCount: Math.ceil(addressesToPrint.length / labelsPerPage),
    blobUrl: returnBlob ? doc.output('bloburl') : null
  };
};

/**
 * Helper to render address text with dynamic centering
 */
/**
 * Helper to render address text with dynamic centering and auto-scaling
 */
function renderAddress(doc, address, x, y, dimensions, baseFontSize) {
  const paddingH = 3; // Horizontal padding

  // Prepare Lines
  const lines = [
    address.name,
    address.address,
    address.address2,
    `${address.city || ''}, ${address.state || ''} ${address.zip || ''}`.trim() === ',' ? null : `${address.city || ''}, ${address.state || ''} ${address.zip || ''}`
  ].filter(Boolean);

  if (lines.length === 0) return;

  // --- Auto-Scaling Logic ---
  // Option 1: Manual Override
  let isCrowded = address.isLittle === true;
  let useManualOverride = isCrowded;
  const maxWidth = dimensions.labelWidth - (paddingH * 2);

  // Option 2: Auto-Detection (only if not manually set)
  if (!isCrowded) {
    doc.setFontSize(baseFontSize); 
    
    let totalVisualLinesStandard = 0;
    lines.forEach(line => {
      const wrappedLines = doc.splitTextToSize(line, maxWidth);
      totalVisualLinesStandard += wrappedLines.length;
    });

    isCrowded = totalVisualLinesStandard >= 4 || lines.length >= 4;
  }
  
  // Settings based on decision
  const fontSize = isCrowded ? 8 : baseFontSize;
  const spacingFactor = isCrowded ? 0.95 : 1.2;
  
  if (isCrowded) {
  }

  // --- Final Metric Calculation ---
  // Now set the actual font size we will use, and calculate ANY wrapping that happens at THAT size.
  doc.setFontSize(fontSize);
  const fontSizeMm = (fontSize * 0.3527); 
  const actualLineHeight = fontSizeMm * spacingFactor;
  
  // We need to build the final list of lines to render (including the wrapped ones)
  // so we can calculate total height accurately.
  let finalLinesToRender = [];
  lines.forEach(line => {
    const wrapped = doc.splitTextToSize(line, maxWidth);
    finalLinesToRender.push(...wrapped);
  });
  
  const totalTextBlockHeight = (finalLinesToRender.length * actualLineHeight);
  
  // Vertical Centering formula
  const verticalSpace = dimensions.labelHeight - totalTextBlockHeight;
  const topPadding = Math.max(0, verticalSpace / 2); 
  
  // Baseline adjustment
  let currentY = y + topPadding + (fontSizeMm * 0.75); 

  finalLinesToRender.forEach(line => {
    doc.text(line, x + paddingH, currentY); 
    // Note: No maxWidth needed in text() because we already split it manually.
    currentY += actualLineHeight;
  });
}

/**
 * Helper to draw debug guides
 */
function drawDebugGuides(doc, x, y, dim, index, template) {
  doc.setDrawColor(255, 0, 0);
  doc.setLineWidth(0.3);
  if (template.debug?.cornerRadius) {
    doc.roundedRect(x, y, dim.labelWidth, dim.labelHeight, template.debug.cornerRadius, template.debug.cornerRadius);
  } else {
    doc.rect(x, y, dim.labelWidth, dim.labelHeight);
  }
}

function drawPageGrid(doc, page) {
    // Optional: Add grid drawing logic here if needed
    // Currently omitted as per previous implementation logic
}
